"use client";

import { getFullLanguageName } from "@faire/web--source/common/consts/THREE_TO_FULL_LANGUAGE_NAME";
import { getTwoLetterLanguageCode } from "@faire/web--source/common/consts/THREE_TO_TWO_LETTER_LANGUAGE_CODE";
import {
  hasDuplicateLanguages,
  isLanguageDuplicatedInLocales,
} from "@faire/web--source/common/localization/languageOptions";
import { localeKeyToFullLanguageName } from "@faire/web--source/common/localization/localeKeyToFullLanguageName";
import { localeKeyToLocaleString } from "@faire/web--source/common/localization/localeKeyToLocaleString";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { Global } from "@faire/web--source/slate/icons/foundation/Global";
import { Typography } from "@faire/web--source/slate/Typography";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import { ILanguageSelectorBaseProps } from "./common";
import { LanguageDropdown } from "./LanguageDropdown";

export const MobileLanguageSelector: React.FC<ILanguageSelectorBaseProps> = (
  props
) => {
  const [isShowingLanguageSelector, setIsShowingLanguageSelector] =
    useState(false);

  const toggleLanguageSelector = useCallback(() => {
    setIsShowingLanguageSelector(
      (isShowingLanguageSelector) => !isShowingLanguageSelector
    );
  }, []);

  const availableLocalesHasDuplicateLanguages = useMemo(
    () => hasDuplicateLanguages(props.availableLocales),
    [props.availableLocales]
  );

  const selectedText = useMemo(() => {
    if (
      !availableLocalesHasDuplicateLanguages ||
      props.browserLocale === undefined
    ) {
      return props.displayFullLanguageName
        ? getFullLanguageName(props.browserLanguage)
        : getTwoLetterLanguageCode(props.browserLanguage);
    }

    if (props.browserLocale.language === Language.LANGUAGE_UNKNOWN) {
      return "";
    }

    const includeCountry =
      !!props.browserLocale &&
      props.browserLocale.language &&
      isLanguageDuplicatedInLocales(
        Language[props.browserLocale.language],
        props.availableLocales
      );

    return props.displayFullLanguageName
      ? localeKeyToFullLanguageName(
          props.browserLocale,
          includeCountry,
          props.localeCountryUrlPrefix
        )
      : localeKeyToLocaleString({
          localeKey: props.browserLocale,
          includeCountry: includeCountry,
          lowercaseLanguage: false,
          localeCountryUrlPrefix: props.localeCountryUrlPrefix,
        });
  }, [
    availableLocalesHasDuplicateLanguages,
    props.availableLocales,
    props.browserLanguage,
    props.browserLocale,
    props.displayFullLanguageName,
    props.localeCountryUrlPrefix,
  ]);

  return (
    <LanguageSelectorDropdownWrapper>
      <Toggle
        onClick={toggleLanguageSelector}
        $hideGlobalIcon={props.hideGlobalIcon}
      >
        {props.hideGlobalIcon ? null : <Global />}
        <Typography>{selectedText}</Typography>
        <Chevron
          className="chevron"
          direction={isShowingLanguageSelector ? "up" : "down"}
        />
      </Toggle>
      {isShowingLanguageSelector ? (
        <LanguageDropdown
          {...props}
          eventName="mobile-view-language-selector"
        />
      ) : null}
    </LanguageSelectorDropdownWrapper>
  );
};

const LanguageSelectorDropdownWrapper = styled.div`
  position: relative;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web--source/slate/Toggle/Toggle#Toggle"
 */
const Toggle = styled.button<{ $hideGlobalIcon?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  width: 100%;
  cursor: pointer;
  height: 36px;
  justify-content: space-between;
  margin-top: 8px;
  margin-bottom: 8px;
  min-width: 100px;
  & svg {
    font-size: 12px;
  }
  & .chevron {
    margin-left: 12px;
  }
  & p {
    ${({ $hideGlobalIcon }) =>
      $hideGlobalIcon ? undefined : "margin-left: 12px;"}
    margin-right: auto;
  }
`;
